/* @flow */
/* eslint-env es6,browser */

import {
  isDNT,
  getEvaluatedDialup,
  getEffectiveType
} from './utils/browserutil';
import { apiVersion } from './utils';
import JSONP from './jsonp';
import { isFetchSupported, fetchJSON } from './utils/fetch';
import adrotation from './adrotation';
import bm from './bidmanager';
import adserver from './adserver';
import adloader from './adloader';
import storage from './utils/storage';
import adconfig from './adconfig';
import { logError, logInfo, logDebug, setLoggerStart } from './utils/logger';
import { isString } from './utils/coreutils';
import idm from './idproviders/idpmanager';

/**
 * @description Implements ad configuration functionality
 * @exports adconfigUpdater
 * @requires  module:utils
 * @requires module:logger
 */

class _adconfigUpdater {
  _name: string;

  _win: window;

  _doc: Document;

  _expiryTimeoutSec: number;

  _loadTimeout: number;

  _url: string;

  _pageInfo: pageInfo;

  _lastConfigLoadTime: number;

  _siteInfo: siteInfo;

  constructor() {
    this._name = 'adconfigUpdater';
    this._win = window;
    this._doc = document;
    this._expiryTimeoutSec = 20 * 60;
    this._loadTimeout = 3000;
    this._url = '';
    this._lastConfigLoadTime = 0;
  }

  /**
   * session logging
   *
   * @returns {undefined}
   */

  _sessionLog(g: globalParam) {
    const dnt = isDNT() ? 1 : 0;
    // prettier-ignore
    const uh = isString(g.customVars._uh5) ? g.customVars._uh5 : '';
    const dApp = g.isDialup ? '1' : '0';
    const dEval = getEvaluatedDialup(g.isDialup) ? '1' : '0';
    const eType = getEffectiveType();

    const log = `sessLog:uh=${uh}&site=${
      g.phInfo.site
      // eslint-disable-next-line prettier/prettier
    }&dnt=${dnt}&dapp=${dApp}&dnw=${eType}&deval=${dEval}`;

    // Record logger start time
    setLoggerStart();
    // Log session log line
    logInfo(log);
  }

  /**
   * Initialize the object
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: window, d: Document, g: globalParam) {
    this._win = w;
    this._doc = d;
    this._loadTimeout = g.adConfig.loadTimeout;
    this._url = g.adConfig.url;
    this._siteInfo = g.phInfo;
    // initialize all the other components
    adconfig.init(w, d, g);
    adloader.init(w, d, g);
    adserver.init(w, d, g);
    adrotation.init(w, d, g);
    idm.init(w, d, g);
    // send session log
    this._sessionLog(g);
  }

  /**
   * _processConfigData - take cares of updating caches and propagating config change
   *
   * @external adConfig
   * @param {adConfig} data - AdConfig data
   * @returns {undefined}
   * @access private
   */
  _processConfigData(data: adConfig) {
    adconfig.processConfigData(data);
    const config: adConfig = adconfig.get();
    // update in-memory cache in ad config
    this._expiryTimeoutSec = config.settings.configExpirySec;

    // propagate config update to other classes
    adrotation.updateConfig(config.settings.adRotate);
    bm.updateConfig(config.settings.headerBidding);
    idm.updateConfig(config.settings.userIds);
    adserver.updateConfig(config.settings.adServer);
    adloader.updateConfig(config.settings.adloading);

    // update local storage cache
    storage.storeConfig(data, this._siteInfo.site);
  }

  /**
   * _fetchConfig - checks for fetch support and uses fetch instead of jsonp
   *
   * @param {string} url - URL for config
   * @external jsonpConfig
   * @param {jsonpConfig} params - Extra JSONP params
   * @returns {Promise} - Fetch/JSONP promise
   * @access private
   */
  _fetchConfig(url: string, params: jsonpConfig) {
    if (isFetchSupported()) {
      params = params || {};
      params.data = params.data || {};
      params.data.format = 'json';
      return fetchJSON(url, params);
    }
    return JSONP(url, params);
  }

  /**
   * _load - load config from server
   *
   * @param {boolean} bFirstLoad - Firstload?
   * @returns {Promise} - Config loading promise
   * @access private
   */
  _load(bFirstLoad: boolean) {
    const params = {};
    params.data = {
      site: this._siteInfo.site,
      apiVersion: apiVersion(),
      brand: this._siteInfo.brand
    };
    params.timeout = this._loadTimeout;
    params.logPrefix = 'adconfig';
    logDebug(`${this._name} Trying to fetch adconfig`);
    return this._fetchConfig(this._url, params)
      .then(
        (data: adConfig) => {
          if (data && data.settings) {
            this._processConfigData(data);
            this._lastConfigLoadTime = Date.now();
          }
          return data;
        },
        (error: string) => {
          logError(
            `Failed to get ad config from ${this._url} for ${
              this._siteInfo.site
            } firstload:${bFirstLoad ? 1 : 0}: ${error || ''}`
          );
        }
      )
      .then(() => Promise.resolve());
  }

  /**
   * loads adconfig from server. If available in cache, will return cache till expiry.
   *
   * @returns {Promise<*>} - Promise
   */
  load(): Promise<void> {
    const bFirstLoad = this._lastConfigLoadTime === 0;
    let expiredCache: boolean = true;
    // get from localstorage for first time
    if (bFirstLoad) {
      const { expired, data, updatedTime } = storage.getConfig(
        this._siteInfo.site
      );
      if (!expired && 'settings' in data) {
        this._processConfigData(data);
        if (Date.now() - updatedTime < this._expiryTimeoutSec * 1000) {
          this._lastConfigLoadTime = updatedTime;
          logDebug(`${this._name} Loaded config from storage for first run!`);
          return Promise.resolve();
        }
      }
      expiredCache = expired;
    }
    const curTime = Date.now();
    if (curTime - this._lastConfigLoadTime < this._expiryTimeoutSec * 1000) {
      logDebug(`${this._name} Last config is good to use!`);
      return Promise.resolve();
    }
    const loadPromise = this._load(bFirstLoad);
    return bFirstLoad && expiredCache ? loadPromise : Promise.resolve();
  }
}

// export singleton object
export default new _adconfigUpdater();
